import { RuleValidationTargetType } from '@atlas/types'
import { useSearchParams } from 'react-router-dom'
import { BackLink } from '@/components/editorial/BackLink'
import { PageHeader } from '@/components/editorial/PageHeader'
import { RuleValidationPanel } from '@/components/rules/RuleValidationPanel'

const TARGET_LABEL: Record<RuleValidationTargetType, string> = {
	[RuleValidationTargetType.FILE]: 'this asset',
	[RuleValidationTargetType.CAMPAIGN]: 'this campaign',
	[RuleValidationTargetType.WORK_ORDER]: 'this work order',
}

const isTargetType = (value: string | null): value is RuleValidationTargetType =>
	Object.values(RuleValidationTargetType).includes(value as RuleValidationTargetType)

/**
 * One target's recorded validation results, as a page of its own - where the applicable
 * rules card's "Validation results" link lands.
 *
 * The target travels in the query string rather than the path because the page is
 * addressed from three kinds of surface and belongs to none of them: it is the results
 * of a run, and a run identifies its target by (type, id).
 */
export function RuleValidationResultsPage() {
	const [searchParams] = useSearchParams()
	const targetTypeParam = searchParams.get('targetType')
	const targetId = searchParams.get('targetId')
	const targetType = isTargetType(targetTypeParam) ? targetTypeParam : null

	return (
		<div>
			<BackLink
				fallbackHref="/rules"
				className="mb-4 inline-flex items-center gap-1 text-ds-sm text-ds-text-muted hover:text-ds-text"
			/>
			<PageHeader
				title="Validation results"
				subtitle={
					targetType
						? `The latest run's verdict for every rule resolving against ${TARGET_LABEL[targetType]}.`
						: undefined
				}
			/>
			{targetType && targetId ? (
				<RuleValidationPanel targetType={targetType} targetId={targetId} groupBySubject />
			) : (
				<p role="alert" className="text-ds-sm text-ds-danger">
					This link names no target to show results for.
				</p>
			)}
		</div>
	)
}
